import { useState } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';

import { useResendVerificationEmail } from 'api/hooks/public/auth/useResendVerificationEmail';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

/**
 * Props for the EmailVerificationStatus component.
 */
interface EmailVerificationStatusProps {
  /** The verification request details */
  verificationRequest?: {
    contact_type: 'email';
    contact_value: string;
    expires_at: string;
    status: 'pending' | 'verified';
    sent_at: string;
    verified_at: string;
  } | null;
}

type Feedback = { message: string; type: 'success' | 'error' } | null;

/**
 * Displays email verification status with a resend button for unverified emails.
 * Shows as helper text below email input fields.
 */
export function EmailVerificationStatus({ verificationRequest }: EmailVerificationStatusProps) {
  const [feedback, setFeedback] = useState<Feedback>(null);
  const resendMutation = useResendVerificationEmail();

  if (!verificationRequest) return null;

  const { status, contact_value: email, verified_at, expires_at } = verificationRequest;

  const handleResend = async () => {
    setFeedback(null);
    try {
      await resendMutation.mutateAsync({ email });
      setFeedback({ message: 'Verification email sent!', type: 'success' });
    } catch (err) {
      setFeedback({ message: getApiErrorMessage(err), type: 'error' });
    }
  };

  // email has been verified
  if (status === 'verified') {
    const verifiedDate = verified_at ? dayjs(verified_at).format('MM/DD/YYYY') : '';
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
        <Typography variant="caption" color="success.main" lineHeight={1}>
          Email verified{verifiedDate ? ` on ${verifiedDate}` : ''}
        </Typography>
      </Stack>
    );
  }

  // email verification is pending
  if (status === 'pending') {
    // check if verification link has expired
    const isExpired = expires_at ? dayjs().isAfter(dayjs(expires_at)) : false;

    // show error icon if expired otherwise show warning icon
    const Icon = isExpired ? ErrorIcon : WarningIcon;
    const iconColor = isExpired ? 'error.main' : 'warning.main';
    const message = isExpired
      ? 'Verification link expired'
      : `Verification pending${expires_at ? ` - expires ${dayjs(expires_at).format('MM/DD/YYYY')}` : ''}`;

    return (
      <Stack spacing={0.5} mt={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Icon sx={{ fontSize: 16, color: iconColor }} />
          <Typography variant="caption" color={iconColor} lineHeight={1}>
            {message}
          </Typography>
          <Button
            size="small"
            onClick={handleResend}
            disabled={resendMutation.isPending}
            sx={{ minWidth: 'auto', py: 0, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Resend verification
          </Button>
        </Stack>
        {feedback && (
          <Typography variant="caption" color={`${feedback.type}.main`}>
            {feedback.message}
          </Typography>
        )}
      </Stack>
    );
  }

  return null;
}
